import React, { useState, useEffect, useRef } from "react";
import Layout from "../Common/Layout";
import ContactInformation from "./ContactInformation";
import DriverInformation from "./DriverInformation";
import VehicleInformation from "./VehicleInformation";
import PolicyCoverage from "./PolicyCoverage";
import Review from "./Review";
import ProcessingApplication from "./ProcessingApplication";
import CompareQuotes from "./CompareQuotes";
import PaymentScreen from "./PaymentScreen";
import PolicyNumberScreen from "./PolicyNumber";
import DocumentESignatureScreen from "./DocumentESignatureScreen";
import PolicySuccessScreen from "./PolicySuccessScreen";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toast } from "primereact/toast";

const STATE_ABBREVIATIONS: Record<string, string> = {
  Alabama: "AL", Alaska: "AK", Arizona: "AZ", Arkansas: "AR", California: "CA",
  Colorado: "CO", Connecticut: "CT", Delaware: "DE", Florida: "FL", Georgia: "GA",
  Hawaii: "HI", Idaho: "ID", Illinois: "IL", Indiana: "IN", Iowa: "IA",
  Kansas: "KS", Kentucky: "KY", Louisiana: "LA", Maine: "ME", Maryland: "MD",
  Massachusetts: "MA", Michigan: "MI", Minnesota: "MN", Mississippi: "MS", Missouri: "MO",
  Montana: "MT", Nebraska: "NE", Nevada: "NV", "New Hampshire": "NH", "New Jersey": "NJ",
  "New Mexico": "NM", "New York": "NY", "North Carolina": "NC", "North Dakota": "ND",
  Ohio: "OH", Oklahoma: "OK", Oregon: "OR", Pennsylvania: "PA", "Rhode Island": "RI",
  "South Carolina": "SC", "South Dakota": "SD", Tennessee: "TN", Texas: "TX", Utah: "UT",
  Vermont: "VT", Virginia: "VA", Washington: "WA", "West Virginia": "WV",
  Wisconsin: "WI", Wyoming: "WY",
};

interface PaymentData {
  data?: {
    paymentLink?: string;
    receiptDatabaseId?: string;
  };
}

interface WorkflowStep {
  id: number;
  title: string;
  shortTitle: string;
  component: React.ComponentType<any>;
  completed: boolean;
}

const InsuranceWorkflow: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [saveLoading, setSaveLoading] = useState(false);
  const toast = React.useRef<Toast>(null);
  const [databaseId, setDatabaseId] = useState<string>("");
  // Get transactionId from URL parameters
  const searchParams = new URLSearchParams(location.search);
  const [transactionId, setTransactionId] = useState<string | null>(() => {
    return searchParams.get("transactionId") || null;
  });
  const [commercialName, setCommercialName] = useState<string | null>(null);
  const [insuredType, setInsuredType] = useState<string | null>(null);
  const [type, setType] = useState<string>("");
  // Robustly get quoteFlow: from navigation state, then localStorage, then default to 'full'
  let quoteFlow = location.state?.quoteFlow;

  if (!quoteFlow) {
    quoteFlow = localStorage.getItem("ancillaryFlow") === "true" ? "ancillary" : "full";
  }
  const [workflowData, setWorkflowData] = useState<Record<string, any>>({});
  const [quoteId, setQuoteId] = useState<string>("");

  const steps: WorkflowStep[] = [
    { id: 1, title: "Contact Information", shortTitle: "Contact", component: ContactInformation, completed: false },
    { id: 2, title: "Driver Information", shortTitle: "Driver", component: DriverInformation, completed: false },
    { id: 3, title: "Vehicle Information", shortTitle: "Vehicle", component: VehicleInformation, completed: false },
    { id: 4, title: "Policy Coverage", shortTitle: "Policy Coverage", component: PolicyCoverage, completed: false },
    { id: 5, title: "Review Details", shortTitle: "Review", component: Review, completed: false },
    { id: 6, title: "Processing Application", shortTitle: "Process", component: ProcessingApplication, completed: false },
    { id: 7, title: "Compare Quotes", shortTitle: "Quotes", component: CompareQuotes, completed: false },
    { id: 8, title: "Payment", shortTitle: "Payment", component: PaymentScreen, completed: false },
    { id: 9, title: "Policy Number", shortTitle: "Policy Number", component: PolicyNumberScreen, completed: false },
    { id: 10, title: "Document E-Signature", shortTitle: "E-Signature", component: DocumentESignatureScreen, completed: false },
    { id: 11, title: "Policy Success", shortTitle: "Success", component: PolicySuccessScreen, completed: false },
  ];

  // Determine initial step based on quoteFlow
  // Defensive: ensure CompareQuotes step exists for ancillary, else fallback to 0
  let initialStep = -1;
  if (typeof location.state?.stepIndex === "number") {
    initialStep = location.state.stepIndex;
  } else if (quoteFlow === "ancillary") {
    const idx = steps.findIndex((step) => step.component && step.component.name === "CompareQuotes");
    initialStep = idx !== -1 ? idx : 0;
  }

  const [currentStep, setCurrentStep] = useState(initialStep);
  const [showSuccess, setShowSuccess] = useState(false);
  const [stepsState, setStepsState] = useState(steps);
  const [loading, setLoading] = useState(false);
  const [insuredId, setInsuredId] = useState<string>(() => {
    // Try to get from location state first, then from localStorage
    const fromLocation = location.state?.selectedClient?.databaseId;
    const fromStorage = localStorage.getItem("currentInsuredId");
    return fromLocation || fromStorage || "";
  });
  const [insuredDetailsLoading, setInsuredDetailsLoading] = useState(false);
  const [storedQuoteNumber, setStoredQuoteNumber] = useState<string>("");
  const quoteNumberRef = useRef<string>("");
  const [persistentQuoteNumber, setPersistentQuoteNumber] = useState<string>("");
  const [selectedClient, setSelectedClient] = useState<any>("");
  const [loadingAncillaryCards, setLoadingAncillaryCards] = useState(false);

  // useEffect(() => {
  //   // Save insuredId to localStorage when it changes
  //   if (insuredId) {
  //     localStorage.setItem("currentInsuredId", insuredId);
  //   }
  // }, [insuredId]);
  useEffect(() => {
    // Save insuredId to localStorage when it changes
    if (location?.state?.quoteType == "Ancillary Quote") {
      localStorage.setItem("ancillaryFlow", "true");
    }
  }, []);
  useEffect(() => {
    // Prevent navigation to step before quote details are loaded
    getQuoteDetailsByTransactionId();
    // Do not setCurrentStep or navigate here; let getQuoteDetailsByTransactionId handle it after data is loaded
  }, [transactionId]);
  // useEffect(() => {
  //   if (insuredId && localStorage.getItem("ancillaryFlow") === "true") {
  //     getAncillaryPoliciesList();
  //   }
  // }, [insuredId]);
  const safeJsonParse = (value: any) => {
    if (!value || value === "" || value === "undefined") return null;

    try {
      const parsed = JSON.parse(value);
      return typeof parsed === "string" ? JSON.parse(parsed) : parsed;
    } catch {
      return null;
    }
  };

  const parseDriverInfo = (value: any) => {
    const parsed = safeJsonParse(value);
    return Array.isArray(parsed) ? parsed : [];
  };
  const mapVehicles = (vehicles: any[]) =>
    vehicles.map((d, index) => ({
      id: d.id ?? index + 1,
      isExpanded: false,
      vin: d.vin ?? "",
      year: d.year ?? "",
      make: d.make ?? "",
      model: d.model ?? "",
      purchaseDate: d.purchaseDate ?? "",
      dailyMileage: d.dailyMileage ?? "",
      annualMileage: d.annualMileage ?? "",
      telematics: d.telematics ?? "",
      comprehensiveLimit: d.comprehensiveLimit ?? "",
      comprehensiveDeductible: d.comprehensiveDeductible ?? "",
      collisionLimit: d.collisionLimit ?? "",
      collisionDeductible: d.collisionDeductible ?? "",
      rentalLimit: d.rentalLimit ?? "",
      rentalDeductible: d.rentalDeductible ?? "",
      towingLaborLimit: d.towingLaborLimit ?? "",
      towingLaborDeductible: d.towingLaborDeductible ?? "",
      customEquipmentLimit: d.customEquipmentLimit ?? "",
      customEquipmentDeductible: d.customEquipmentDeductible ?? "",
      gapLimit: d.gapLimit ?? "",
      gapDeductible: d.gapDeductible ?? "",
      usage: d.usage ?? "",
      milesToWork: d.milesToWork ?? "",
      assignedDriverId: d.assignedDriverId ?? "",
      safetyGlassCoverage: d.safetyGlassCoverage ?? "",
    }));
  const mapDrivers = (drivers: any[]) =>
    drivers.map((d, index) => ({
      id: d.id ?? index + 1,
      isExpanded: false,

      driverType: d.driverType ?? "",
      relationship: d.relationship ?? "",

      firstName: d.firstName ?? "",
      lastName: d.lastName ?? "",
      dateOfBirth: d.dateOfBirth ?? "",

      gender: d.gender ?? "",
      maritalStatus: d.maritalStatus ?? "",
      education: d.education ?? "",
      occupation: d.occupation ?? "",
      yearsEmployed: d.yearsEmployed ?? "",
      monthsEmployed: d.monthsEmployed ?? "",
      milesToWork: d.milesToWork ?? "",
      annualMiles: d.annualMiles ?? "",

      dlNumber: d.dlNumber ?? "",
      dlState: d.dlState ?? "",
      dlYear: d.dlYear ?? "",

      accidentsOrClaims: d.accidentsOrClaims ?? "",
      accidentViolationCode: d.accidentViolationCode ?? "",
      accidentViolationDate: d.accidentViolationDate ?? "",
      violations: Array.isArray(d.violations) ? d.violations : [],

      sr22: d.sr22 ?? "",
      sr22State: d.sr22State ?? "",
      sr22Reason: d.sr22Reason ?? "",
      sr22Date: d.sr22Date ?? "",

      countryOfOrigin: d.countryOfOrigin ?? "",
      licenseStatus: d.licenseStatus ?? "",
      internationalDriversLicense: d.internationalDriversLicense ?? "",
    }));

  const getQuoteDetailsByTransactionId = () => {
    setLoading(true);
    setInsuredDetailsLoading(true);
    // Reset quote number state before loading a new transaction to avoid showing stale data
    setPersistentQuoteNumber("");
    setStoredQuoteNumber("");
    quoteNumberRef.current = "";
    localStorage.removeItem("currentQuoteNumber");

    axios({
      method: "get",
      url: `${process.env.REACT_APP_ACE_API_Url}/api/ace/getquotehistorybytransactionid/${transactionId}`,
      headers: {
        Authorization: `Bearer ${localStorage.getItem("igtoken")}`,
        "Content-Type": "application/json",
      },
    })
      .then((response: any) => {
        setLoading(false);

        const isNewQuote = response.status === 204 || !response.data;

        if (isNewQuote) {
          localStorage.setItem("currentInsuredId", insuredId);
          setInsuredId(insuredId);
        } else {
          localStorage.setItem("currentInsuredId", response.data.insuredId);
          setInsuredId(response.data.insuredId);
        }
        const apiSavedStep = response.data?.currentStep ? parseInt(response.data.currentStep) : 0;
        if (insuredId == "null" || insuredId === "" || insuredId === null) {
          // Only set step after data is loaded
          if (initialStep === -1) {
            setCurrentStep(apiSavedStep);
            if (apiSavedStep > 0) {
              setStepsState((prev) =>
                prev.map((step, index) => ({
                  ...step,
                  completed: index < apiSavedStep,
                })),
              );
            }
          }
        } else {
          getInsuredDetails(localStorage.getItem("NCToken"), localStorage.getItem("currentInsuredId"), response.data, isNewQuote, apiSavedStep);
          if (localStorage.getItem("currentInsuredId") && localStorage.getItem("ancillaryFlow") === "true") {
            getAncillaryPoliciesList();
          }
        }

        if (response.status !== 204 && response.data) {
          const apiData = response.data;

          try {
            /** CONTACT INFO */
            const parsedContact = JSON.parse(apiData.contactInformationJson);
            const contactInfo = typeof parsedContact === "string" ? JSON.parse(parsedContact) : parsedContact;
            const parsedPolicyNumberDetails = JSON.parse(apiData.policyDetailsJson);
            const policyDetailsInfo =
              typeof parsedPolicyNumberDetails === "string" ? JSON.parse(parsedPolicyNumberDetails) : parsedPolicyNumberDetails;

            /** DRIVER INFO */
            const driverRaw = parseDriverInfo(apiData.driverInformationJson);
            const drivers = mapDrivers(driverRaw);

            const vehicleRaw = parseDriverInfo(apiData.vehicleInformationJson);

            // Check if non-owner policy flag is present in vehicle data
            let isNonOwnerPolicy = false;
            let vehicles: any[] = [];

            if (Array.isArray(vehicleRaw) && vehicleRaw.length === 1 && vehicleRaw[0].isNonOwnerPolicy) {
              // Non-owner policy case: extract the flag and set vehicles to empty
              isNonOwnerPolicy = vehicleRaw[0].isNonOwnerPolicy;
              vehicles = [];
            } else {
              // Normal case: map vehicles
              vehicles = mapVehicles(vehicleRaw);
            }

            let newSignDocumentFileIds: any = [];
            newSignDocumentFileIds = safeJsonParse(apiData.signDocumentFileIds) || [];

            const policyCoverage = safeJsonParse(apiData.policyCoverageInformationJson) || {};

            const insuranceQuotes = safeJsonParse(apiData.insuranceQuotesJson) || {};

            const turboraterQuotes = safeJsonParse(apiData.turboraterQuotesJson) || {};

            if (apiData?.quoteNumber) {
              quoteNumberRef.current = apiData.quoteNumber;
              setPersistentQuoteNumber(apiData.quoteNumber);
            }

            // Parse paymentDataJson if present
            let paymentData: PaymentData | undefined = undefined;
            if (apiData.paymentDataJson) {
              try {
                paymentData = JSON.parse(apiData.paymentDataJson);
              } catch (e) {
                paymentData = undefined;
              }
            }
            setWorkflowData((prev) => ({
              ...prev,
              isNewQuote: false,
              isNonOwnerPolicy: isNonOwnerPolicy,

              storedQuoteNumber: apiData?.quoteNumber || "",
              quoteId: apiData?.quoteId || "",
              /** STEP 1 — Contact ONLY */
              step1: {
                firstName: contactInfo?.firstName || "",
                lastName: contactInfo?.lastName || "",
                dateOfBirth: contactInfo?.dateOfBirth ? contactInfo.dateOfBirth.split("T")[0] : "",
                cellphone: contactInfo?.cellphone || "",
                homePhone: contactInfo?.homePhone || "",
                email: contactInfo?.email || "",
                address: contactInfo?.address || "",
                city: contactInfo?.city || "",
                state: contactInfo?.state || "",
                zipCode: contactInfo?.zipCode || "",
                county: contactInfo?.county || "",
                databaseId: contactInfo?.databaseId || "",
                policyType: contactInfo?.policyType || "",
                yearsAtResidence: contactInfo?.yearsAtResidence !== undefined ? Number(contactInfo.yearsAtResidence) : undefined,
                monthsAtResidence: contactInfo?.monthsAtResidence !== undefined ? Number(contactInfo.monthsAtResidence) : undefined,
                residenceType: contactInfo?.residenceType || "",
                residenceStatus: contactInfo?.residenceStatus || "",

                priorInsurance: contactInfo?.priorInsurance || "",
                timeWithPriorInsuranceYear: contactInfo?.timeWithPriorInsuranceYear || "",
                timeWithPriorInsuranceMonth: contactInfo?.timeWithPriorInsuranceMonth || "",

                priorExpirationDate: contactInfo?.priorExpirationDate ? contactInfo.priorExpirationDate.split("T")[0] : "",

                priorInsuranceCarrier: contactInfo?.priorInsuranceCarrier || "",
                priorLiabilityLimits: contactInfo?.priorLiabilityLimits || "",
                priorAddress: contactInfo?.priorAddress || "",
                priorCity: contactInfo?.priorCity || "",
                priorState: contactInfo?.priorState || "",
                priorZipCode: contactInfo?.priorZipCode || "",
                serviceMessageAgreed: contactInfo?.serviceMessageAgreed ?? false,
                optInEmail: contactInfo?.optInEmail || "",
                optInPhone: contactInfo?.optInPhone || "",
                optInText: contactInfo?.optInText || "",
                emailOptInStatus: contactInfo?.emailOptInStatus || "",
                phoneOptInStatus: contactInfo?.phoneOptInStatus || "",
                textOptInStatus: contactInfo?.textOptInStatus || "",
                // identifier: contactInfo?.identifier || "",
                propertyInsurance: contactInfo?.propertyInsurance || "",
                customerDeclinedCredit: contactInfo?.customerDeclinedCredit || "",

                street2: contactInfo?.street2 || "",
                reasonForNoInsurance: contactInfo?.reasonForNoInsurance || "",
                customerHasPriorWithThisAgency: contactInfo?.customerHasPriorWithThisAgency || "",
              },

              /** STEP 2 — Drivers ONLY */
              step2: drivers,
              step3: vehicles,
              step4: policyCoverage,
              signDocumentFileIds: newSignDocumentFileIds,
              step6: {
                quotes: turboraterQuotes,
              },
              step7: insuranceQuotes,
              step8: {
                ...(prev.step8 || {}),
                paymentData,
              },
              step9: policyDetailsInfo,
            }));

            setQuoteId(apiData?.quoteId || "");
            // localStorage.setItem("currentQuoteId", apiData?.quoteId || "");
            setStoredQuoteNumber(apiData?.quoteNumber || "");
            setWorkflowData((prev) => ({ ...prev, quoteId: apiData?.quoteId || "" }));

            // Only set step after data is loaded
            const savedStep = apiData.currentStep ? parseInt(apiData.currentStep) : 0;
            if (initialStep === -1) {
              setCurrentStep(savedStep);
              if (savedStep > 0) {
                setStepsState((prev) =>
                  prev.map((step, index) => ({
                    ...step,
                    completed: index < savedStep,
                  })),
                );
              }
            } else {
              setStepsState((prev) =>
                prev.map((step, index) => ({
                  ...step,
                  completed: index < initialStep,
                })),
              );
            }
          } catch (e) {
            console.error("Failed to parse API quote data", e);
          }
        }
      })
      .catch((error: any) => {
        if (error.response?.status === 401) {
          localStorage.clear();
          navigate("/");
        } else {
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail:
              error.response && error.response.status
                ? `Error ${error.response.status}: ${error.response.data?.message || "An error occurred while getting  the quote details"}`
                : "Unknown error while getting  the quote details!",
            life: 3000,
          });
        }
      });
  };
  const getInsuredTagsList = (insuredDataBaseId: string) => {
    let data = JSON.stringify({
      insuredDataBaseId: [insuredDataBaseId],
    });

    axios({
      method: "post",
      url: `${process.env.REACT_APP_ACE_API_Url}/api/ace/insuredtags?ncToken=${localStorage.getItem("NCToken")}  `,
      headers: {
        Authorization: `Bearer ${localStorage.getItem("igtoken")}`,
        "Content-Type": "Application/json",
      },

      data: data,
    })
      .then((response: any) => {
        const tagsList = response.data;

        let optInEmailValue = "";
        let optInPhoneValue = "";
        let optInTextValue = "";

        let optInEmailType = "";
        let optInPhoneType = "";
        let optInTextType = "";

        if (Array.isArray(tagsList)) {
          tagsList.forEach((tag: any) => {
            const tagName = tag.name || "";

            if (tagName.toLowerCase().includes("marketing preferences")) {
              const parts = tagName.split(":").map((part: string) => part.trim());

              if (parts.length >= 3) {
                const type = parts[1].toLowerCase();
                const option = parts[2];

                if (option.toLowerCase() === "opt-out") {
                  if (type === "email") {
                    optInEmailType = "opt-out";
                  } else if (type === "phone") {
                    optInPhoneType = "opt-out";
                  } else if (type === "text") {
                    optInTextType = "opt-out";
                  }
                } else if (parts.length === 4) {
                  const value = parts[3];
                  if (type === "email") {
                    optInEmailValue = value;
                    optInEmailType = "opt-in";
                  } else if (type === "phone") {
                    optInPhoneValue = value;
                    optInPhoneType = "opt-in";
                  } else if (type === "text") {
                    optInTextValue = value;
                    optInTextType = "opt-in";
                  }
                }
              }
            }
          });
        }

        // Update workflow data with parsed communication preferences
        setWorkflowData((prev: any) => ({
          ...prev,
          step1: {
            ...prev.step1,
            optInEmail: optInEmailValue,
            optInPhone: optInPhoneValue,
            optInText: optInTextValue,
            emailOptInStatus: optInEmailType,
            phoneOptInStatus: optInPhoneType,
            textOptInStatus: optInTextType,
          },
        }));
      })
      .catch((error: any) => {
        if (error.response?.status === 401) {
          localStorage.clear();
          navigate("/");
        } else {
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail:
              error.response && error.response.status
                ? `Error ${error.response.status}: ${error.response.data?.message || "An error occurred while searching the client!"}`
                : "Unknown error while inserting the insured tags!",
            life: 3000,
          });
        }
      });
  };

  const getAncillaryPoliciesList = () => {
    setLoadingAncillaryCards(true);
    axios({
      method: "get",
      url: `${process.env.REACT_APP_ACE_API_Url}/api/ace/policies?insuredId=${insuredId}&ncToken=${localStorage.getItem("NCToken")}`,
      headers: {
        Authorization: `Bearer ${localStorage.getItem("igtoken")}`,
        "Content-Type": "Application/json",
      },
    })
      .then((response: any) => {
        const policies = response.data.value || [];
        const cards = policies.map((policy: any) => ({
          id: policy.databaseId,
          policyNumber: policy.number,
          effectiveDate: policy.effectiveDate,
          totalPremium: policy.totalPremium,
        }));
        setWorkflowData((prev: any) => ({
          ...prev,
          step6: { ancillaryCards: cards },
        }));
        setLoadingAncillaryCards(false);
      })
      .catch((error: any) => {
        setLoadingAncillaryCards(false);
        if (error.response?.status === 401) {
          localStorage.clear();
          navigate("/");
        } else {
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail:
              error.response && error.response.status
                ? `Error ${error.response.status}: ${error.response.data?.message || "An error occurred while getting the ancillary policies!"}`
                : "Unknown error while getting the ancillary policies!",
            life: 3000,
          });
        }
      });
  };

  const getInsuredDetails = (ncToken: any, insuredId: any, responseData: any, isNewQuote: boolean = false, savedStep: number = 0) => {
    axios({
      method: "get",
      url: `${process.env.REACT_APP_ACE_API_Url}/api/ace/insured?insuredId=${insuredId}&nowCertsToken=${ncToken}  `,
      headers: {
        Authorization: `Bearer ${localStorage.getItem("igtoken")}`,
      },
    })
      .then((response: any) => {
        const apiData = response.data.value[0];
        setSelectedClient(response.data.value[0]);
        if (responseData == null || responseData === undefined || responseData === "") {
          setWorkflowData((prev) => ({
            ...prev,
            isNewQuote: isNewQuote,
            quoteId: "",
            step1: {
              firstName: apiData?.firstName || "",
              lastName: apiData?.lastName || "",
              dateOfBirth: apiData?.dateOfBirth ? apiData.dateOfBirth.split("T")[0] : "",
              cellphone: apiData?.cellPhone || "",
              homePhone: apiData?.phone || "",
              email: apiData?.eMail || "",
              address: apiData?.addressLine1 || "",
              city: apiData?.city || "",
              state: apiData?.state || "",
              zipCode: apiData?.zipCode || "",
              county: apiData?.county || "",
              databaseId: apiData?.databaseId || "",
            },
          }));
          getInsuredTagsList(response.data.value[0].databaseId);
        }
        setCommercialName(apiData?.commercialName || "");
        setInsuredType(apiData?.insuredType || "");
        setType(apiData?.type || "");
        setDatabaseId(apiData?.databaseId || "");
        if (initialStep === -1) {
          setCurrentStep(savedStep);
          if (savedStep > 0) {
            setStepsState((prev) =>
              prev.map((step, index) => ({
                ...step,
                completed: index < savedStep,
              })),
            );
          }
        }
        setInsuredDetailsLoading(false);
      })
      .catch((error: any) => {
        setInsuredDetailsLoading(false);
        if (error.response?.status === 401) {
          localStorage.clear();
          navigate("/");
        } else {
        }
      });
  };

  const handleNextStep = async (data?: any) => {
    const shouldSkipSave = data && data.skipSave === true;

    let actualData = data;
    let isNonOwnerPolicyFromVehicle = false;

    if (currentStep === 2 && data && typeof data === "object" && "vehicles" in data) {
      actualData = data.vehicles;
      isNonOwnerPolicyFromVehicle = data.isNonOwnerPolicy || false;
    }

    let updatedData = actualData ? { ...workflowData, [`step${currentStep + 1}`]: actualData } : workflowData;
    if (data && typeof data === "object" && "vehicleCount" in data) {
      updatedData = {
        ...updatedData,
        step7: {
          ...(updatedData.step7 || {}),
          vehicleCount: data.vehicleCount,
        },
      };
    }

    // if (localStorage.getItem("ancillaryFlow") === "true" && currentStep === 6) {
    //   updatedData = {
    //     ...updatedData,
    //     quoteId: data?.selectedQuotes[0],
    //   };
    // }
    if (localStorage.getItem("ancillaryFlow") === "true" && currentStep === 6) {
      updatedData = {
        ...updatedData,
        quoteId: data?.selectedData?.selectedQuotes?.[0],
      };
    }

    if (localStorage.getItem("ancillaryFlow") === "true" && currentStep === 7) {
      updatedData = {
        ...updatedData,
        quoteId: data?.quoteId,
      };
    }
    // Add isNonOwnerPolicy flag to updatedData if from vehicle step
    if (currentStep === 2) {
      updatedData.isNonOwnerPolicy = isNonOwnerPolicyFromVehicle;
    }

    if (actualData) {
      setWorkflowData((prev: any) => {
        let updates = { ...prev, [`step${currentStep + 1}`]: actualData };
        // Store isNonOwnerPolicy flag if from vehicle step
        if (currentStep === 2) {
          updates.isNonOwnerPolicy = isNonOwnerPolicyFromVehicle;
        }

        // PATCH: Set vehicleCount in workflowData.step7 if present in data
        if (data && typeof data === "object" && "vehicleCount" in data) {
          updates = {
            ...updates,
            step7: {
              ...(updates.step7 || {}),
              vehicleCount: data.vehicleCount,
            },
          };
        }
        return updates;
      });
    }

    // Save data for Contact, Driver, Vehicle, and Policy Coverage steps (steps 0-3)
    if (currentStep >= 0 && currentStep <= 3) {
      // Generate quote number once to ensure consistency across all API calls
      const quoteNumber = ensureQuoteNumber();

      let insertedInsuredId = "";
      if (insuredId == "null" || insuredId === "" || insuredId === null) {
        insertedInsuredId = await UpsertInsuredDetails(updatedData?.step1 || {});
        setInsuredId(insertedInsuredId);
        setDatabaseId(insertedInsuredId);

        if (insertedInsuredId) {
          const step1 = updatedData?.step1 || {};
          // const primaryDriver = Array.isArray(updatedData?.step2) ? updatedData.step2[0] : null;
          const insuredContactPayload = {
            database_id: "00000000-0000-0000-0000-000000000000",
            first_name: step1.firstName || "",
            middle_name: "",
            last_name: step1.lastName || "",
            description: "",
            type: "Insured",
            personal_email: step1.email || "",
            business_email: "",
            home_phone: step1.homePhone || "",
            office_phone: "",
            cell_phone: step1.cellphone || "",
            personal_fax: "",
            business_fax: "",
            ssn: "",
            birthday: step1.dateOfBirth || "",
            insured_database_id: insertedInsuredId,
            insured_email: step1.email || "",
            insured_first_name: step1.firstName || "",
            insured_last_name: step1.lastName || "",
            insured_commercial_name: commercialName || "",
            marital_status: "",
            gender:  "",
            match_record_base_on_name: false,
            is_driver: false,
            dl_number:  "",
            dl_state: "",
            is_primary: true,
          };
          axios
            .post(
              `${process.env.REACT_APP_ACE_API_Url}/api/ace/insuredcontact?ncToken=${localStorage.getItem("NCToken")}`,
              insuredContactPayload,
              {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem("igtoken")}`,
                  "Content-Type": "application/json",
                },
              },
            )
            .catch((contactError: any) => {
              console.error("Error inserting insured contact:", contactError);
            });
        }
      }

      let quoteId = "";
      if (currentStep == 0) {
        quoteId = await InsertQuote(false, "", quoteNumber, workflowData.quoteId, insertedInsuredId);
      }
      if (currentStep == 3) {
        quoteId = await InsertQuote(true, updatedData?.step4 || {}, quoteNumber, workflowData.quoteId);
        if (updatedData?.step3.length > 0) {
          await UpsertPolicyCoverages(updatedData?.step4 || {}, updatedData?.step3 || []);
        }
      }
      if (quoteId) {
        updatedData.quoteId = quoteId;
        setWorkflowData((prev) => ({ ...prev, quoteId }));
      }

      if (!shouldSkipSave) {
        await handleSaveData(updatedData, quoteNumber);
      }
      // if (
      //   currentStep == 0 &&
      //   (updatedData?.step1?.emailOptInStatus == "opt-out" ||
      //     updatedData?.step1?.phoneOptInStatus == "opt-out" ||
      //     updatedData?.step1?.textOptInStatus == "opt-out")
      // ) {
      //   RemoveInsuredTags();
      // }
      if (currentStep == 0) {
        let optValues = [];
        let optInValue = "Marketing Preferences : ";

        if (updatedData?.step1?.emailOptInStatus == "opt-in") {
          optInValue += "email : opt-in : " + updatedData?.step1?.optInEmail;
        } else {
          optInValue += "email : opt-out";
        }
        optValues.push(optInValue);
        optInValue = "Marketing Preferences : ";
        if (updatedData?.step1?.phoneOptInStatus == "opt-in") {
          optInValue += "phone : opt-in : " + updatedData?.step1?.optInPhone;
        } else {
          optInValue += "phone : opt-out";
        }
        optValues.push(optInValue);
        optInValue = "Marketing Preferences : ";
        if (updatedData?.step1?.textOptInStatus == "opt-in") {
          optInValue += "text : opt-in : " + updatedData?.step1?.optInText;
        } else {
          optInValue += "text : opt-out";
        }
        optValues.push(optInValue);
        InsertInsuredTags(optValues, insertedInsuredId);
      }
      if (currentStep == 0 && insuredId !== "null" && insuredId !== null) UpsertInsuredDetails(updatedData?.step1 || {});
      if (currentStep == 1) updateDriverList(updatedData?.step2 || {}, quoteNumber, workflowData.quoteId);
      if (currentStep == 2 && !updatedData.isNonOwnerPolicy) {
        updateVehicleList(updatedData?.step3 || {}, quoteNumber, workflowData.quoteId);
      }
    } else {
      // Only save data for steps outside 0-3 (like review, payment, etc.)
      if (!shouldSkipSave) {
        await handleSaveData(updatedData, ensureQuoteNumber());
      }
    }

    // For ancillary flow, set the quoteId to the selected policy ID (from CompareQuotes)
    if (data && (data.SourceQuoteId || data.quoteId || (data.selectedData && (data.selectedData.SourceQuoteId || data.selectedData.quoteId)))) {
      const newQuoteId = data.SourceQuoteId || data.quoteId || (data.selectedData && (data.selectedData.SourceQuoteId || data.selectedData.quoteId));
      setWorkflowData((prev) => ({ ...prev, quoteId: newQuoteId }));
      setQuoteId(newQuoteId);
    }

    if (data && data.signDocumentFileIds) {
      setWorkflowData((prev) => ({ ...prev, signDocumentFileIds: data.signDocumentFileIds }));
    }

    if (data && data.carrierName) {
      setWorkflowData((prev) => ({ ...prev, step4: { ...(prev.step4 || {}), carrier: data.carrierName } }));
    }

    if (currentStep == 4) {
      setWorkflowData((prev) => ({ ...prev, step6: { quotes: [] } }));
    }

    // Mark current step as completed
    const updatedSteps = stepsState.map((step, index) => (index === currentStep ? { ...step, completed: true } : step));
    setStepsState(updatedSteps);

    // If coming from ContactInformation and ancillaryFlow is true, jump to CompareQuotes step
    if (data && data.ancillaryFlow === true) {
      // Find the index of CompareQuotes step
      const quotesStepIndex = stepsState.findIndex((step) => step.component && step.component.name === "CompareQuotes");
      if (quotesStepIndex !== -1) {
        setCurrentStep(quotesStepIndex);
        return;
      }
    }

    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };
  const handleSaveData = async (updatedWorkflowData: any, quoteNumber?: string): Promise<void> => {
    setSaveLoading(true);
    // Use provided quote number or ensure one exists
    const finalQuoteNumber = quoteNumber || ensureQuoteNumber();

    // Extract data from each step
    const contactInfo = updatedWorkflowData.step1 || {};
    const driverInfo = updatedWorkflowData.step2 || {};
    const vehicleInfo = updatedWorkflowData.step3 || {};
    const policyCoverageInfo = updatedWorkflowData.step4 || {};
    const insuranceQuotes = updatedWorkflowData.step7 || "";
    const policyDetailsInfo = updatedWorkflowData.step9 || {};
    const turboraterQuotes = updatedWorkflowData?.step6?.quotes || {};

    // Check if non-owner policy is selected
    const isNonOwnerPolicy = updatedWorkflowData.isNonOwnerPolicy || false;

    // Send empty array for vehicleInformationJson if non-owner policy is selected
    const vehicleJsonData = isNonOwnerPolicy ? JSON.stringify([{ isNonOwnerPolicy: isNonOwnerPolicy }]) : JSON.stringify(vehicleInfo);

    let statusToSend = updatedWorkflowData.status || "In Progress";
    // For step 10 (Document E-Signature), use step10.status if present
    if (currentStep === 9 && updatedWorkflowData.step10 && updatedWorkflowData.step10.status) {
      statusToSend = updatedWorkflowData.step10.status;
    }
    if (currentStep === 10 && updatedWorkflowData.step11 && updatedWorkflowData.step11.status) {
      statusToSend = updatedWorkflowData.step11.status;
    }
    if (currentStep === 6 && localStorage.getItem("ancillaryFlow") === "true" && updatedWorkflowData.step11 && updatedWorkflowData.step11.status) {
      statusToSend = updatedWorkflowData.step11.status;
    }

    let latestQuoteId = updatedWorkflowData.quoteId || quoteId;
    if (localStorage.getItem("ancillaryFlow") === "true" && currentStep === 6) {
      latestQuoteId = updatedWorkflowData?.step7?.selectedQuotes;
    }

    // If payment is completed, preserve the step at which payment was completed (step 7)
    // Don't update currentStep when navigating backwards after payment completion
    // But allow moving forward to steps beyond payment (8, 9, 10, 11)
    const isPaymentCompleted = updatedWorkflowData.step8?.paymentCompleted === true || !!workflowData.step8?.paymentData?.data;
    const stepToSave = isPaymentCompleted && currentStep < 7 ? "7" : String(currentStep);

    let data = JSON.stringify({
      transactionId: transactionId ?? null,
      clientName:
        contactInfo.firstName && contactInfo.lastName ? `${contactInfo.firstName} ${contactInfo.lastName}` : updatedWorkflowData.clientName || "",
      email: contactInfo.email || updatedWorkflowData?.step1?.email || "",
      phone: contactInfo.cellphone || updatedWorkflowData?.step1?.cellphone || contactInfo.homePhone || updatedWorkflowData?.step1?.homePhone || "",
      address: contactInfo.address || updatedWorkflowData?.step1?.address || "",
      insuredId: insuredId,
      quoteId: latestQuoteId,
      // If SourceQuoteId is present in workflowData, use it as quoteId for ancillary flow
      ...(workflowData.SourceQuoteId ? { quoteId: workflowData.SourceQuoteId } : {}),
      contactInformationJson: JSON.stringify(contactInfo),
      driverInformationJson: JSON.stringify(driverInfo),
      vehicleInformationJson: vehicleJsonData,
      policyCoverageInformationJson: JSON.stringify(policyCoverageInfo),
      policyDetailsJson: JSON.stringify(policyDetailsInfo),
      status: statusToSend || "In Progress",
      currentStep: stepToSave,
      quoteNumber: finalQuoteNumber,
      quoteType: quoteFlow === "ancillary" ? "Ancillary Quote" : "Full Quote",
      insuranceQuotesJson: JSON.stringify(insuranceQuotes),
      turboraterQuotesJson: JSON.stringify(turboraterQuotes),
    });

    var config = {
      method: "post",
      url: `${process.env.REACT_APP_ACE_API_Url}/api/ace/addorupdatequotehistory`,
      headers: {
        Authorization: `Bearer ${localStorage.getItem("igtoken")}`,
        "Content-Type": "application/json",
      },
      data: data,
    };

    try {
      await axios(config);
      setSaveLoading(false);
      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: "Quote history saved successfully!",
        life: 3000,
      });
      if (statusToSend == "Completed" && currentStep !== 9) {
        navigate("/search-client");
      }
    } catch (error: any) {
      setSaveLoading(false);
      if (error.response && error.response.status === 401) {
        localStorage.clear();
        navigate("/");
      } else {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail:
            error.response && error.response.status
              ? `Error ${error.response.status}: ${error.response.data?.message || "An error occurred while saving the quote history!"}`
              : "Unknown error while saving the quote history!",
          life: 3000,
        });
      }
      throw error;
    }
  };
  const generateQuoteNumber = () => {
    const now = new Date();

    // US Date: MMDDYYYY
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const dd = String(now.getDate()).padStart(2, "0");
    const yyyy = now.getFullYear();

    // 24-hour time (HHMMSS)
    const hh = String(now.getHours()).padStart(2, "0");
    const min = String(now.getMinutes()).padStart(2, "0");
    const sec = String(now.getSeconds()).padStart(2, "0");

    return `Q-${mm}${dd}${yyyy}-${hh}${min}${sec}`;
  };

  const InsertInsuredTags = (optInValue: any, insertedInsuredId?: string) => {
    let data = JSON.stringify({
      insuredDataBaseId: [databaseId || insertedInsuredId],
      OptValues: optInValue,
    });

    axios({
      method: "post",
      url: `${process.env.REACT_APP_ACE_API_Url}/api/ace/insertinsuredtags?ncToken=${localStorage.getItem("NCToken")}`,
      headers: {
        Authorization: `Bearer ${localStorage.getItem("igtoken")}`,
        "Content-Type": "Application/json",
      },

      data: data,
    })
      .then((response: any) => {})
      .catch((error: any) => {
        if (error.response?.status === 401) {
          localStorage.clear();
          navigate("/");
        } else {
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail:
              error.response && error.response.status
                ? `Error ${error.response.status}: ${error.response.data?.message || "An error occurred while removing the insured tags!"}`
                : "Unknown error while removing the insured tags!",
            life: 3000,
          });
        }
      });
  };

  // Reusable function to ensure quote number exists
  const ensureQuoteNumber = () => {
    let stored = localStorage.getItem("currentQuoteNumber");
    if (stored) {
      if (!persistentQuoteNumber) {
        setPersistentQuoteNumber(stored);
        quoteNumberRef.current = stored;
        setStoredQuoteNumber(stored);
      }
      return stored;
    }
    if (!persistentQuoteNumber) {
      const newQuoteNumber = generateQuoteNumber();
      localStorage.setItem("currentQuoteNumber", newQuoteNumber);
      setPersistentQuoteNumber(newQuoteNumber);
      quoteNumberRef.current = newQuoteNumber;
      setWorkflowData((prev) => ({ ...prev, storedQuoteNumber: newQuoteNumber }));
      setStoredQuoteNumber(newQuoteNumber);
      return newQuoteNumber;
    }
    return persistentQuoteNumber;
  };

  const InsertQuote = async (
    isUpdate: boolean = false,
    updatedStep4Data: any = {},
    quoteNumber?: string,
    quoteId?: string,
    insertedInsuredId?: string,
  ): Promise<string> => {
    setSaveLoading(true);

    // Use provided quote number or ensure one exists
    const finalQuoteNumber = quoteNumber || ensureQuoteNumber();

    let data = JSON.stringify({
      databaseId: quoteId,
      insuredDatabaseId: insuredId == "null" || insuredId === "" || insuredId === null ? insertedInsuredId : insuredId,
      insuredFirstName: workflowData?.step1?.firstName || "",
      insuredLastName: workflowData?.step1?.lastName || "",
      insuredCommercialName: commercialName || "",
      insuredEmail: workflowData?.step1?.email || "",
      // effectiveDate: updatedStep4Data.policyEffectiveDate ? String(updatedStep4Data.policyEffectiveDate) : "",
      effectiveDate: (() => {
        const raw = updatedStep4Data.policyEffectiveDate ? String(updatedStep4Data.policyEffectiveDate) : "";
        if (!raw) return "";
        const toMMDDYYYY = (d: Date) => {
          const mm = String(d.getMonth() + 1).padStart(2, "0");
          const dd = String(d.getDate()).padStart(2, "0");
          const yyyy = d.getFullYear();
          return `${mm}/${dd}/${yyyy}`;
        };
        const date = new Date(raw);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return date < today ? toMMDDYYYY(today) : toMMDDYYYY(date);
      })(),
      expirationDate: updatedStep4Data.policyExpirationDate ? String(updatedStep4Data.policyExpirationDate) : "",
      bindDate: "",
      lineOfBusinessName: "Personal Auto",
      premium: "",
      carrierName: workflowData?.step4?.carrier || "",
      quoteNumber: finalQuoteNumber,
      stateAbbreviationCode: STATE_ABBREVIATIONS[workflowData?.step1?.state || ""] || workflowData?.step1?.state || "",
    });

    var config = {
      method: "post",
      url: `${process.env.REACT_APP_ACE_API_Url}/api/ace/insertquote?ncToken=${localStorage.getItem("NCToken")}`,
      headers: {
        Authorization: `Bearer ${localStorage.getItem("igtoken")}`,
        "Content-Type": "application/json",
      },
      data: data,
    };

    try {
      const response = await axios(config);
      setSaveLoading(false);
      const newQuoteId = response.data.quoteId;
      setQuoteId(newQuoteId);
      setWorkflowData((prev) => ({ ...prev, quoteId: newQuoteId }));
      // localStorage.setItem("currentQuoteId", newQuoteId);
      return newQuoteId;
    } catch (error: any) {
      setSaveLoading(false);
      if (error.response && error.response.status === 401) {
        localStorage.clear();
        navigate("/");
      } else {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail:
            error.response && error.response.status
              ? `Error ${error.response.status}: ${error.response.data?.message || "An error occurred while saving the quote history!"}`
              : "Unknown error while saving the insured quote!",
          life: 3000,
        });
      }
      throw error;
    }
  };
  const UpsertPolicyCoverages = async (updatedStep4Data: any = {}, vehiclesData: any[] = []): Promise<void> => {
    setSaveLoading(true);
    const payload = vehiclesData.map((vehicle) => ({
      insured_database_id: insuredId,
      insured_commercial_name: commercialName,
      insured_email: workflowData?.step1?.email || "",
      insured_first_name: workflowData?.step1?.firstName || "",
      insured_last_name: workflowData?.step1?.lastName || "",
      vehicle_database_id: vehicle.databaseId || "00000000-0000-0000-0000-000000000000",
      bodily_injury_limit: updatedStep4Data.bodilyInjuryLimit || "",
      bodily_injury_premium: null,
      property_damage_limit: updatedStep4Data.propertyDamageLimit || "",
      property_damage_premium: null,
      umbi_limit: updatedStep4Data.umbiLimit || "",
      umbi_premium: null,
      uimbi_limit: updatedStep4Data.uimbiLimit || "",
      uimbi_premium: null,
      umpd_limit: updatedStep4Data.uninsuredPdLimit || "",
      umpd_premium: null,
      medical_payments_limit: updatedStep4Data.medicalPaymentLimit || "",
      medical_payments_premium: null,
      pip_limit: updatedStep4Data.pipDed || "",
      pip_premium: null,
      accidental_death_limit: updatedStep4Data.accidentalDeathLimit || "",
      accidental_death_premium: null,
      comprehensive_limit: vehicle.comprehensiveLimit || "",
      comprehensive_premium: null,
      collision_limit: vehicle.collisionLimit || "",
      collision_premium: null,
      rental_limit: vehicle.rentalLimit || "",
      rental_premium: null,
      towing_labor_limit: vehicle.towingLaborLimit || "",
      towing_labor_premium: null,
      custom_equipment_limit: vehicle.customEquipmentLimit || "",
      custom_equipment_premium: null,
      full_glass_limit: "",
      full_glass_premium: null,
      gap_limit: vehicle.gapLimit || "",
      gap_premium: null,
    }));

    const data = JSON.stringify(payload);

    var config = {
      method: "post",
      url: `${process.env.REACT_APP_ACE_API_Url}/api/ace/upsert-vehicle-coverages?ncToken=${localStorage.getItem("NCToken")}`,
      headers: {
        Authorization: `Bearer ${localStorage.getItem("igtoken")}`,
        "Content-Type": "application/json",
      },
      data: data,
    };

    try {
      await axios(config);
      setSaveLoading(false);
    } catch (error: any) {
      setSaveLoading(false);
      if (error.response && error.response.status === 401) {
        localStorage.clear();
        navigate("/");
      } else {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail:
            error.response && error.response.status
              ? `Error ${error.response.status}: ${error.response.data?.message || "An error occurred while saving the policy coverage!"}`
              : "Unknown error while saving the policy coverage!",
          life: 3000,
        });
      }
      throw error;
    }
  };
  const UpsertInsuredDetails = async (updatedStep1Data: any = {}): Promise<string> => {
    setSaveLoading(true);

    const data = {
      databaseId: databaseId == "" || databaseId == null ? "00000000-0000-0000-0000-000000000000" : databaseId,
      firstName: updatedStep1Data?.firstName || "",
      lastName: updatedStep1Data?.lastName || "",
      commercialName: commercialName || "",
      eMail: updatedStep1Data?.email || "",
      addressLine1: updatedStep1Data?.address || "",
      addressLine2: updatedStep1Data?.address2 || "",
      dateOfBirth: updatedStep1Data?.dateOfBirth || "",
      city: updatedStep1Data?.city || "",
      state: updatedStep1Data?.state || "",
      zipCode: updatedStep1Data?.zipCode || "",
      phone: updatedStep1Data?.homePhone || "",
      cellPhone: updatedStep1Data?.cellphone || "",
      insuredType: insuredType || "",
      type: type == "Insured" ? 0 : 1,
      insuredId: insuredId || "",
      county: updatedStep1Data?.county || "",
    };

    try {
      const response = await axios.post(`${process.env.REACT_APP_ACE_API_Url}/api/ace/insured?ncToken=${localStorage.getItem("NCToken")}`, data, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("igtoken")}`,
          "Content-Type": "application/json",
        },
      });

      setSaveLoading(false);
      return response.data.insuredDatabaseId || "";
    } catch (error: any) {
      setSaveLoading(false);

      if (error.response?.status === 401) {
        localStorage.clear();
        navigate("/");
      } else {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: error.response?.status
            ? `Error ${error.response.status}: ${error.response.data?.message || "An error occurred while saving the quote history!"}`
            : "Unknown error while saving the insured quote!",
          life: 3000,
        });
      }

      return "";
    }
  };

  const mapEducationToValue = (education: string): number => {
    if (!education || education.trim() === "") {
      return 0;
    }

    const trimmedEducation = education.trim();

    const educationMap: Record<string, number> = {
      "No High School Diploma": 0,
      "High School Diploma": 1,
      "Some College - No Degree": 2,
      "Vocational - Technical Degree": 3,
      "Associates Degree": 4,
      Bachelors: 5,
      Masters: 6,
      PhD: 7,
      "Medical Degree": 8,
      "Law Degree": 9,
    };

    const mappedValue = educationMap[trimmedEducation];

    return mappedValue !== undefined ? mappedValue : 0;
  };
  const generateGuid = () => {
    return crypto.randomUUID();
  };
  const updateDriverList = (driversData: any[] = [], quoteNumber?: string, quoteId?: string) => {
    setSaveLoading(true);

    const addDriverModels = driversData.map((driver) => ({
      database_id: driver.databaseId ?? "",
      insured_database_id: insuredId,
      insured_commercial_name: commercialName,
      insured_email: workflowData?.step1?.email || "",
      insured_first_name: workflowData?.step1?.firstName || "",
      insured_last_name: workflowData?.step1?.lastName || "",
      //policy_numbers: [quoteNumber],
      driver_type: driver.driverType ?? "",
      relationship_to_applicant_code: driver.relationship ?? "",
      first_name: driver.firstName ?? "",
      last_name: driver.lastName ?? "",
      date_of_birth: driver.dateOfBirth || null,
      policy_database_id: quoteId || "",
      license_number: driver.dlNumber ?? "",
      license_state: driver.dlState ?? "",
      license_year: driver.dlYear ?? "",

      excluded: false,
      active: true,

      gender: driver.gender ?? "",

      driver_license_class: driver.driverLicenseClass ? parseInt(driver.driverLicenseClass) : 0,

      hire_date: null,
      // cdl_date: null,
      cdl_year: driver.dlYear ?? "",

      termination_date: null,

      address_line_1: null,
      city: null,
      driver_address_state: driver.dlState ?? "",
      zip_code: null,
      marital_status: driver.maritalStatus ?? "",

      education: mapEducationToValue(driver.education ?? ""),
      driver_occupations: Array.isArray(driver.occupations) ? driver.occupations : driver.occupation ? [driver.occupation] : [],
      driver_relationship_to_applicant_code: driver.relationship ?? "",
    }));

    const data = JSON.stringify(addDriverModels);

    var config = {
      method: "post",
      url: `${process.env.REACT_APP_ACE_API_Url}/api/ace/addorupdatedrivers?ncToken=${localStorage.getItem("NCToken")}`,
      headers: {
        Authorization: `Bearer ${localStorage.getItem("igtoken")}`,
        "Content-Type": "application/json",
      },
      data: data,
    };

    axios(config)
      .then((response: any) => {
        setSaveLoading(false);
        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: "Drivers saved successfully!",
          life: 3000,
        });
      })
      .catch((error: any) => {
        setSaveLoading(false);
        if (error.response && error.response.status === 401) {
          localStorage.clear();
          navigate("/");
        } else {
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail:
              error.response && error.response.status
                ? `Error ${error.response.status}: ${error.response.data?.message || "An error occurred while saving the vehicles!"}`
                : "Unknown error while saving the drivers!",
            life: 3000,
          });
        }
      });
  };
  const updateVehicleList = (vehiclesData: any[] = [], quoteNumber?: string, quoteId?: string) => {
    setSaveLoading(true);

    // Helper function to safely parse numeric values for limits
    const parseLimit = (value: any): number[] => {
      if (!value || value === "" || value === "None" || value === "No Coverage") {
        return [];
      }
      const parsed = parseFloat(value);
      return !isNaN(parsed) ? [parsed] : [];
    };

    const payload = vehiclesData.map((vehicle) => ({
      database_id: vehicle.databaseId ?? "",
      insured_database_id: insuredId,
      insured_commercial_name: commercialName,
      insured_email: workflowData?.step1?.email || "",
      insured_first_name: workflowData?.step1?.firstName || "",
      insured_last_name: workflowData?.step1?.lastName || "",
      // policy_numbers: [quoteNumber],
      policy_database_id: quoteId || "",
      vin: vehicle.vin ?? "",
      make: vehicle.make ?? "",
      model: vehicle.model ?? "",
      year: vehicle.year ?? "",
      type: vehicle.type ?? "",
      deductible_comprehensive: vehicle.comprehensiveDeductible == "None" ? 0 : parseInt(vehicle.comprehensiveDeductible) || 0,
      deductible_collision: vehicle.collisionDeductible == "None" ? 0 : parseInt(vehicle.collisionDeductible) || 0,
      vehicle_specific_coverages: [
        {
          name: "Rental Reimbursement",
          typeCode: "RREIM",
          limits: parseLimit(vehicle.rentalLimit),
          limitFormatted: vehicle.rentalLimit ?? "",
          premium: 0.0,
        },
        {
          name: "Towing and Labor",
          typeCode: "TL",
          limits: parseLimit(vehicle.towingLaborLimit),
          limitFormatted: vehicle.towingLaborLimit ?? "",
          premium: 0.0,
        },
        {
          name: "Audio, Visual and Data Equipment Coverage",
          typeCode: "AVD",
          limits: parseLimit(vehicle.customEquipmentLimit),
          limitFormatted: vehicle.customEquipmentLimit ?? "",
          premium: 0.0,
        },
        {
          name: "Lease/Loan Gap",
          typeCode: "GAP",
          limits: parseLimit(vehicle.gapLimit),
          limitFormatted: vehicle.gapLimit ? vehicle.gapLimit : "Yes",
          premium: 0.0,
        },
      ],
      vehicleEditAdditional4:
        vehicle.purchaseDate || vehicle.annualMileage || vehicle.dailyMileage
          ? {
              purchase_date: vehicle.purchaseDate ? vehicle.purchaseDate.split("T")[0] : null,
              estimated_annual_miles: vehicle.annualMileage ? vehicle.annualMileage : null,
              miles_driven_on_way_to_work: vehicle.dailyMileage ? vehicle.dailyMileage : null,
              use: vehicle.usage ? vehicle.usage : null,
            }
          : null,
    }));

    const data = JSON.stringify(payload);

    var config = {
      method: "post",
      url: `${process.env.REACT_APP_ACE_API_Url}/api/ace/addorupdatevehicles?ncToken=${localStorage.getItem("NCToken")}`,
      headers: {
        Authorization: `Bearer ${localStorage.getItem("igtoken")}`,
        "Content-Type": "application/json",
      },
      data: data,
    };

    axios(config)
      .then((response: any) => {
        // Set databaseId for each vehicle in step3 using response.data array
        setWorkflowData((prev) => {
          const prevStep3 = Array.isArray(prev.step3) ? prev.step3 : [];
          const updatedStep3 = prevStep3.map((vehicle: any, idx: number) => ({
            ...vehicle,
            databaseId: response.data[idx]?.databaseId || vehicle.databaseId,
          }));
          return { ...prev, step3: updatedStep3 };
        });

        updateVehicleCoverages(vehiclesData, response.data);

        setSaveLoading(false);
        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: "Vehicles saved successfully!",
          life: 3000,
        });
      })
      .catch((error: any) => {
        setSaveLoading(false);
        if (error.response && error.response.status === 401) {
          localStorage.clear();
          navigate("/");
        } else {
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail:
              error.response && error.response.status
                ? `Error ${error.response.status}: ${error.response.data?.message || "An error occurred while saving the vehicles!"}`
                : "Unknown error while saving the vehicles!",
            life: 3000,
          });
        }
      });
  };

  const updateVehicleCoverages = (vehiclesData: any[] = [], responseData: any[] = []) => {
    setSaveLoading(true);
    const payload = vehiclesData.map((vehicle, index) => ({
      insured_database_id: insuredId,
      insured_commercial_name: commercialName,
      insured_email: workflowData?.step1?.email || "",
      insured_first_name: workflowData?.step1?.firstName || "",
      insured_last_name: workflowData?.step1?.lastName || "",
      vehicle_database_id: responseData[index]?.databaseId ?? "",
      gap_limit: vehicle.gapLimit ?? "",
      custom_equipment_limit: vehicle.customEquipmentLimit ?? "",
      towing_labor_limit: vehicle.towingLaborLimit ?? "",
      rental_limit: vehicle.rentalLimit ?? "",
    }));

    const data = JSON.stringify(payload);

    var config = {
      method: "post",
      url: `${process.env.REACT_APP_ACE_API_Url}/api/ace/upsert-vehicle-coverages?ncToken=${localStorage.getItem("NCToken")}`,
      headers: {
        Authorization: `Bearer ${localStorage.getItem("igtoken")}`,
        "Content-Type": "application/json",
      },
      data: data,
    };

    axios(config)
      .then((response: any) => {
        setSaveLoading(false);
        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: "Vehicles coverages saved successfully!",
          life: 3000,
        });
      })
      .catch((error: any) => {
        setSaveLoading(false);
        if (error.response && error.response.status === 401) {
          localStorage.clear();
          navigate("/");
        } else {
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail:
              error.response && error.response.status
                ? `Error ${error.response.status}: ${error.response.data?.message || "An error occurred while saving the vehicles!"}`
                : "Unknown error while saving the vehicles!",
            life: 3000,
          });
        }
      });
  };
  const handlePreviousStep = () => {
    // Detect Quotes step by index for strict match, using navigation state if present
    let quotesStepIndex = steps.findIndex((step) => step.component && step.component.name === "CompareQuotes");
    if (typeof location.state?.stepIndex === "number") {
      quotesStepIndex = location.state.stepIndex;
    }
    const isQuotesStep = currentStep === quotesStepIndex;
    // Only check localStorage for ancillary flow
    const isAncillary = localStorage.getItem("ancillaryFlow") === "true";

    // If on first step (Contact Information), go back to select-quote-flow
    if (currentStep === 0) {
      navigate("/select-quote-flow", {
        state: {
          selectedClient: selectedClient,
        },
      });
      return;
    }

    if (currentStep == 6 && isAncillary) {
      navigate("/select-quote-flow", {
        state: {
          selectedClient: selectedClient,
        },
      });
      return;
    }
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStepClick = (stepIndex: number) => {
    if (stepIndex <= currentStep || stepsState[stepIndex - 1]?.completed) {
      setCurrentStep(stepIndex);
    }
  };

  const handleNavigateToStep = (stepIndex: number) => {
    setCurrentStep(stepIndex);
  };

  // Defensive: prevent undefined errors if currentStep is out of bounds or component is missing
  let CurrentComponent = null;
  if (
    Array.isArray(stepsState) &&
    currentStep >= 0 &&
    currentStep < stepsState.length &&
    stepsState[currentStep] &&
    stepsState[currentStep].component
  ) {
    CurrentComponent = stepsState[currentStep].component;
  }

  // If CurrentComponent is null, show a fallback error UI
  if (!CurrentComponent) {
    return (
      <Layout currentPage="Workflow Error">
        {insuredDetailsLoading ? (
          <div style={{ alignItems: "center", justifyContent: "center", display: "flex" }}>
            <div style={{ width: "50px" }}>
              <svg className="validation-spinner" viewBox="0 0 24 24">
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  fill="none"
                  stroke="#36c0c4"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeDasharray="31.416"
                  strokeDashoffset="31.416"
                >
                  <animate attributeName="stroke-dasharray" dur="2s" values="0 31.416;15.708 15.708;0 31.416" repeatCount="indefinite" />
                  <animate attributeName="stroke-dashoffset" dur="2s" values="0;-15.708;-31.416" repeatCount="indefinite" />
                </circle>
              </svg>
            </div>
          </div>
        ) : (
          <div style={{ padding: 40, textAlign: "center", color: "#e11d48", fontWeight: 600 }}>
            Sorry, there was a problem loading this workflow step.
            <br />
            <span style={{ fontSize: 15, color: "#64748b", fontWeight: 400 }}>Please return to the previous page or restart the quote flow.</span>
          </div>
        )}
      </Layout>
    );
  }

  const contactName = [workflowData?.step1?.firstName, workflowData?.step1?.lastName].filter(Boolean).join(" ");
  const displayQuoteNumber = persistentQuoteNumber || storedQuoteNumber || "";

  return (
    <Layout currentPage={stepsState[currentStep].title}>
      <div className="insurance-workflow">
        {/* Contact Name & Quote Number */}
        {(contactName || displayQuoteNumber) && (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: "16px",
              padding: "8px 16px",
              backgroundColor: "#f8fafc",
              borderBottom: "1px solid #e2e8f0",
              borderRadius: "8px 8px 0 0",
              fontSize: "13px",
              color: "#475569",
            }}
          >
            {contactName && (
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#64748b"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                <span style={{ fontWeight: 600, color: "#1e293b" }}>{contactName}</span>
              </div>
            )}
            {contactName && displayQuoteNumber && <span style={{ color: "#cbd5e1" }}>|</span>}
            {displayQuoteNumber && (
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#64748b"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                </svg>
                <span style={{ fontWeight: 600, color: "#1e293b" }}>{displayQuoteNumber}</span>
              </div>
            )}
          </div>
        )}

        {/* Compact Stepper */}
        <div className="compact-stepper">
          <div className="stepper-progress-bar">
            <div className="stepper-progress-fill" style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }} />
          </div>

          <div className="stepper-nav">
            {stepsState.map((step, index) => (
              <div
                key={step.id}
                className={`stepper-item ${index === currentStep ? "active" : step.completed ? "completed" : "pending"}`}
                style={{ cursor: "default" }}
                // onClick={() => handleStepClick(index)}
              >
                <div className="stepper-circle">
                  {step.completed ? (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" stroke="currentColor" />
                    </svg>
                  ) : (
                    <span>{step.id}</span>
                  )}
                </div>
                <span className="stepper-label">{step.shortTitle}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Page Content */}
        <div className="workflow-content">
          <CurrentComponent
            onNext={handleNextStep}
            onPrevious={handlePreviousStep}
            workflowData={workflowData}
            currentStep={currentStep + 1}
            totalSteps={steps.length}
            isFirstStep={currentStep === 0}
            isLastStep={currentStep === steps.length - 1}
            showChangeClient={currentStep === 0}
            carrier={(workflowData as any).carrier ? (workflowData as any).carrier : ""}
            onNavigateToStep={handleNavigateToStep}
            selectedState={workflowData.step1?.state || location.state?.selectedState || ""}
            insuredId={insuredId}
            loading={saveLoading}
            loadingAncillaryCards={loadingAncillaryCards}
          />
        </div>
      </div>
    </Layout>
  );
};

export default InsuranceWorkflow;
